// @ts-check
// Miniatura de um e-mail (spec/email.md seção 19.1): a prévia num iframe sandbox="" com srcdoc, reduzida a 35%, só
// quando o cartão aparece na tela. O modelo da lista vem sem o documento: a miniatura busca o modelo quando fica
// visível e guarda o desenho por id e data de alteração.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { getEmailTemplate } from '../../data/email-templates.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { SandboxedHtmlFrame } from './sandboxed-html-frame.js';
import { browserBaseUrl, renderLocalPreview } from './local-preview.js';
import { useEmailBranding } from './email-branding.js';

/**
 * @typedef {import('../../../../shared/email-design/email-document.js').EmailDocument} EmailDocument
 * @typedef {{ document: EmailDocument, subject: string, preheader: string, purpose: 'MARKETING' | 'SERVICE' }} ThumbnailContent
 */

export const THUMBNAIL_SCALE = 0.35;
const FRAME_WIDTH = 680;
const FRAME_HEIGHT = 760;
/** @type {Map<string, { updatedAt: string, content: ThumbnailContent }>} */
const contentCache = new Map();

/** Fica verdadeiro quando o elemento entra na tela (uma vez). */
function useBecameVisible() {
  const ref = useRef(/** @type {HTMLElement | null} */ (null));
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element || isVisible) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { ref, isVisible };
}

/**
 * Miniatura de um conteúdo já em mãos (galeria).
 * @param {{ content: ThumbnailContent, title: string }} props
 */
export function EmailContentThumbnail({ content, title }) {
  const branding = useEmailBranding();
  const { ref, isVisible } = useBecameVisible();
  const source = useMemo(() => (isVisible
    ? renderLocalPreview(content.document, {
      subject: content.subject, preheader: content.preheader, purpose: content.purpose, settings: branding.settings,
      institutionName: branding.institutionName, logoUrl: branding.logoUrl, baseUrl: browserBaseUrl(),
    }).html
    : ''), [isVisible, content, branding.settings, branding.logoUrl]);
  return html`<div ref=${ref} class="email-thumbnail">
    ${source
      ? html`<${SandboxedHtmlFrame} html=${source} title=${title} width=${FRAME_WIDTH} height=${FRAME_HEIGHT} scale=${THUMBNAIL_SCALE} lazy=${true} scrolling=${false} />`
      : html`<${Skeleton} width=${Math.round(FRAME_WIDTH * THUMBNAIL_SCALE)} height=${Math.round(FRAME_HEIGHT * THUMBNAIL_SCALE)} radius=${4} />`}
  </div>`;
}

/**
 * Miniatura de um modelo salvo: busca o documento quando o cartão fica visível.
 * @param {{ templateId: string, updatedAt: string, title: string }} props
 */
export function EmailTemplateThumbnail({ templateId, updatedAt, title }) {
  const { ref, isVisible } = useBecameVisible();
  const cached = contentCache.get(templateId);
  const [content, setContent] = useState(/** @type {ThumbnailContent | null} */ (cached && cached.updatedAt === updatedAt ? cached.content : null));
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!isVisible || content) return undefined;
    let isCancelled = false;
    getEmailTemplate(templateId)
      .then((template) => {
        const next = { document: template.document, subject: template.subject, preheader: template.preheader, purpose: template.purpose };
        contentCache.set(templateId, { updatedAt: template.updatedAt, content: next });
        if (!isCancelled) setContent(next);
      })
      .catch(() => !isCancelled && setFailed(true));
    return () => {
      isCancelled = true;
    };
  }, [isVisible, templateId, updatedAt]);
  return html`<div ref=${ref} class="email-thumbnail-holder">
    ${content
      ? html`<${EmailContentThumbnail} content=${content} title=${title} />`
      : failed
        ? html`<div class="email-thumbnail email-thumbnail-failed"><${Icon} name="IconMail" size=${24} /></div>`
        : html`<div class="email-thumbnail"><${Skeleton} width=${Math.round(FRAME_WIDTH * THUMBNAIL_SCALE)} height=${Math.round(FRAME_HEIGHT * THUMBNAIL_SCALE)} radius=${4} /></div>`}
  </div>`;
}
